import * as THREE from 'three';
import { OrbitControls } from 'orbit-controls';
// Import dynamique de sceneSettings (voir constructeur)
import { RenderingUtils } from '../utils/renderingUtils.js';
import { MaterialManager } from './materials/materialManager.js';
import { Environment } from './environment.js';
import { Renderer } from './renderer.js';
import { MultiModelManager } from './objects/Models/multiModelManager.js';
import { FaceGeo } from './objects/FaceGeo/faceGeo.js';
import { VideoPlane } from './objects/video_bg.js';

/// CONSTRUCTEUR ET INITIALISATION
export class ThreeScene {
    constructor(threeCanvas, videoCanvas, maskCanvas, videoInsert, project_settings, width, height, initialSceneIndex = 0, sceneSettings) {
        // Identifiant unique pour débugger
        this.instanceId = Math.random().toString(36).substr(2, 9);
        //console.log(`🆔 Nouvelle instance ThreeScene créée: ${this.instanceId}`);
        
        this.faceVisible = false;
        this.poseVisible = false; // Ajout de l'état de visibilité pour la pose
        this.width = width;
        this.height = height;
        this.scene = new THREE.Scene();
        this.project_settings = project_settings; // Stocker les settings complets du projet
        this.threeCanvas = threeCanvas;
        this.maskCanvas = maskCanvas;
        this.videoCanvas = videoCanvas;
        this.videoInsert = videoInsert;

        // Utiliser l'index initial passé en paramètre depuis PHP
        this.sceneIndex = initialSceneIndex;
        //console.log(`🎯 ThreeScene initialisée avec l'index de scène: ${this.sceneIndex}`);

        // Stocker les settings passés en paramètre
        this.sceneSettings = sceneSettings;
        this.clock = new THREE.Clock();
        this.lastUpdateTime = 0;
        // Removed guiManager reference to eliminate circular dependency
        this.renderer = new THREE.WebGLRenderer({ 
            canvas: threeCanvas, 
            alpha: true, 
            antialias: true, 
            powerPreference: 'high-performance', 
            context: null, 
            forceWebGL: 2, 
            logarithmicDepthBuffer: false, 
            preserveDrawingBuffer: false,
            precision: 'lowp', // Use medium precision to reduce shader complexity
            stencil: false // Disable stencil buffer if not needed
        });
        this.renderer.shadowMap.enabled = false;
        this.renderer.physicallyCorrectLights = false;
        // Color management: ensure sRGB output
        if ('outputColorSpace' in this.renderer) {
            this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        } else if ('outputEncoding' in this.renderer) {
            // sRGBEncoding deprecated, use SRGBColorSpace if needed
            this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        }
        
        // Configure renderer to reduce shader warnings
        this.renderer.capabilities.precision = 'mediump';
        this.renderer.debug.checkShaderErrors = false; // Disable shader error checking in production
        this.prevVisibilityState = { face: false, pose: false };
        if (this.project_settings.isOrthographic) this.camera = new THREE.OrthographicCamera(width / -2, width / 2, height / 2, height / -2, -2000, 2000);
        else this.camera = new THREE.PerspectiveCamera(project_settings.fov, width / height, 10, 2000);
        this.camera.position.z = 5;
        this.scene.add(this.camera);

        // Initialisation des OrbitControls
        this.controls = new OrbitControls(this.camera, this.threeCanvas);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.25;
        this.controls.screenSpacePanning = false;
        this.controls.maxPolarAngle = Math.PI / 2;

        this.rendererInstance = new Renderer(this.renderer, this.scene, this.camera, this.width, this.height);
        this.multiModelManager = null;
        this.materialManager = null;
        this.onSceneChangedCallback = null;

        this.launchScene();
        this.handleCanvasClick = this.launchContent.bind(this);
        this.threeCanvas.addEventListener('click', this.handleCanvasClick);
        //console.log(`🎯 Event listener de clic ajouté à ThreeScene ${this.instanceId}`);
    }

    // /// INITIALISATION DES PARAMÈTRES GUI
    // initializeModelGUIParameters(guiManager) {
    //     if (!guiManager || !this.multiModelManager) return;
    //     const currentSettings = this.sceneSettings[this.sceneIndex];        
    //     if (currentSettings.models && currentSettings.models.length > 0) {
    //         console.log(`🎛️ Initialisation des paramètres GUI pour la scène ${this.sceneIndex}`);
    //         guiManager.initModelsParameters(currentSettings.models, this.multiModelManager);
    //     }
    // }

    /// CHANGEMENT DE SCÈNE AU CLICK
    async launchContent(event) {
        //console.loglog(`🖱️ Clic détecté sur instance ${this.instanceId} - launchContent appelé`);
        if (this.scene) {
            //console.loglog(`🔄 Changement de scène - Nettoyage en cours... (instance ${this.instanceId})`);
            
            // Nettoyer proprement les gestionnaires avant de supprimer les objets de la scène
            if (this.multiModelManager) {
                this.multiModelManager.clearModels();
                this.multiModelManager = null;
            }
            
            // Nettoyer le renderer et le postprocess
            if (this.rendererInstance) {
                this.rendererInstance.disablePostprocess();
                // Forcer un rendu direct pour nettoyer l'état
                this.renderer.render(this.scene, this.camera);
                
                // Nettoyage supplémentaire si la scène précédente avait du postprocess
                const currentSettings = this.sceneSettings[this.sceneIndex];
                const nextSettings = this.sceneSettings[(this.sceneIndex + 1) % this.sceneSettings.length];
                const currentPostEnabled = Array.isArray(currentSettings.postprocesses) && currentSettings.postprocesses.length > 0;
                const nextPostEnabled = Array.isArray(nextSettings.postprocesses) && nextSettings.postprocesses.length > 0;

                if (currentPostEnabled && !nextPostEnabled) {
                    //console.loglog('🧹 Nettoyage supplémentaire: transition postprocess → normal');
                    this.renderer.clear(true, true, true);
                    this.renderer.resetState();
                } else if (!currentPostEnabled && nextPostEnabled) {
                    //console.loglog('🧹 Nettoyage supplémentaire: transition normal → postprocess');
                    this.renderer.clear(true, true, true);
                    this.renderer.resetState();
                }
            }
            
            // Nettoyer le gestionnaire de matériaux
            if (this.materialManager) {
                this.materialManager.dispose();
                this.materialManager = null;
            }
            
            // Supprimer tous les objets de la scène Three.js
            for (let i = this.scene.children.length - 1; i >= 0; i--) {
                const child = this.scene.children[i];
                // Nettoyer les ressources WebGL avant de supprimer l'objet
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(mat => {
                            if (mat.map) mat.map.dispose();
                            if (mat.alphaMap) mat.alphaMap.dispose();
                            mat.dispose();
                        });
                    } else {
                        if (child.material.map) child.material.map.dispose();
                        if (child.material.alphaMap) child.material.alphaMap.dispose();
                        child.material.dispose();
                    }
                }
                this.scene.remove(child);
            }
            
            // Nettoyer les autres composants
            this.environment = null;
            this.faceGeo = null;
            this.videoBackground = null;
            this.segmentationPlane = null;
            this.directionalLight = null;
            
            this.sceneIndex = (this.sceneIndex + 1) % this.sceneSettings.length;
            //console.loglog(`🎯 Passage à la scène ${this.sceneIndex}`);
            await this.launchScene();
        }
    }

    /// LANCEMENT/CHARGEMENT DE LA SCÈNE
    async launchScene() {
        // console.log("launch scene")
        window.loader.setMessage('Scène prête');
    const currentSettings = this.sceneSettings[this.sceneIndex];
    const postEnabled = Array.isArray(currentSettings.postprocesses) && currentSettings.postprocesses.length > 0;
        
        // IMPORTANT: Désactiver complètement le postprocess AVANT de faire quoi que ce soit d'autre
        this.rendererInstance.disablePostprocess();
        
        // S'assurer que le canvas vidéo est visible par défaut
        const videoCanvas = document.getElementById('videoCanvas');
        if (videoCanvas) videoCanvas.style.display = 'block';
        
        // Créer l'environnement
        this.environment = new Environment(this.scene, currentSettings.environmentUrl, this.threeCanvas, this.videoInsert, this.videoCanvas);
        
        // Créer la directional light si configurée dans les settings
        this.setupDirectionalLight(currentSettings.directionalLight);
        
        // Créer le MaterialManager AVANT d'activer le postprocess
        this.materialManager = new MaterialManager(
            this.scene, 
            this.videoInsert, 
            this.videoCanvas, 
            this.maskCanvas, 
            this.width, 
            this.height, 
            postEnabled, 
            currentSettings.globalMaterialReplacements || {},
            currentSettings.materialCreations || []
        );
        
        // Maintenant activer le postprocess si nécessaire (APRÈS MaterialManager)
        if (postEnabled) {
            this.rendererInstance.enablePostprocess(currentSettings.postprocesses);
            // Mettre à jour le MaterialManager pour qu'il sache que le postprocess est activé
            this.materialManager.updatePostprocessMode(true);
        } else {
            // S'assurer que le MaterialManager sait que le postprocess est désactivé
            this.materialManager.updatePostprocessMode(false);
        }
    
        this.faceGeo = (this.project_settings.faceDetect && currentSettings.faceGeo) ? new FaceGeo(this.scene, this.project_settings, this.width, this.height, this.environment, this.materialManager, postEnabled, currentSettings.faceTextures) : null;
        
        if (currentSettings.models && currentSettings.models.length > 0) {
            this.multiModelManager = new MultiModelManager(this.scene, this.environment, this.materialManager, currentSettings);
            this.multiModelManager.onAllModelsLoaded = () => { if (this.onModelsLoadedCallback) this.onModelsLoadedCallback(); };
            await this.multiModelManager.loadModels(currentSettings.models);
          
        } else {
            this.multiModelManager = null;
            //console.logwarn('⚠️ Aucun modèle configuré dans cette scène');
        }
    this.videoBackground = (postEnabled && this.materialManager) ? new VideoPlane(this.scene, this.width, this.height, this.project_settings, "videoBackground", new THREE.Vector3(0, 0, -1500), "Mask", this.width, this.height, -1) : null;
      // this.videoBackground = (this.materialManager) ? new VideoPlane(this.scene, this.width, this.height, this.project_settings, "videoBackground", new THREE.Vector3(0, 0, -1500), "Mask", this.width, this.height, -1) : null;
       
        const segmentationRef = currentSettings.segmentationPlaneReference;
        // console.log(segmentationRef)
        const hasPoseModels = currentSettings.models && this.project_settings.poseDetect && currentSettings.models.some(model => model.modelType === 'pose');
        // console.log(hasPoseModels)
        if ((segmentationRef && hasPoseModels) && this.project_settings.poseDetect && this.materialManager) {
           this.segmentationPlane = new VideoPlane(this.scene, this.width, this.height, this.project_settings, "segmentationPlane", new THREE.Vector3(0, 0, -5), "segmentationPlaneMaterial", this.width, this.height, 500);
            this.segmentationPlane.plane.visible = false;
            this.segmentationPlaneModelRef = segmentationRef;
            console.log('✅ Plan de segmentation créé et configuré' + (segmentationRef ? ` avec référence au modèle "${segmentationRef}"` : ''));
        } else {
            this.segmentationPlane = null;
            this.segmentationPlaneModelRef = null;
            if (hasPoseModels && !this.project_settings.poseDetect) {
               // console.warn('⚠️ Modèles de type "pose" détectés mais poseDetect est désactivé');
            }
        }
        if (this.materialManager) this.materialManager.applyMaterialReplacements();
        // Les paramètres GUI seront initialisés automatiquement après le chargement des modèles via le callback onAllModelsLoaded configuré plus haut
       window.loader.hide();
        if (this.onSceneChangedCallback) this.onSceneChangedCallback();
    }

    /// UPDATE PRINCIPAL DE LA SCÈNE
    update(coordsResults, settings, width, height) {
         
        // Calculer le deltaTime pour les animations
        const currentTime = this.clock.getElapsedTime();
        const deltaTime = currentTime - this.lastUpdateTime;
        this.lastUpdateTime = currentTime;
        this.faceVisible = this.project_settings.faceDetect ? (coordsResults.face?.faceVisible ?? false) : false;
        this.poseVisible = this.project_settings.poseDetect ? (coordsResults.pose?.poseVisible ?? false) : false;
        // UPDATE MODELS
        if (this.multiModelManager) {
            // this.multiModelManager.handleVisibilityChanges(coordsResults, this.prevVisibilityState);
            this.multiModelManager.updateModels(coordsResults, deltaTime);
            this.prevVisibilityState.face = this.faceVisible;
            this.prevVisibilityState.pose = this.poseVisible;
        } else {
           // console.warn('⚠️ Aucun gestionnaire de modèles disponible');
        }
        // UPDATE SEGMENTATION PLANE
        if (this.segmentationPlane) {
            const wasVisible = this.segmentationPlane.plane.visible;
            if (this.poseVisible) {
                this.segmentationPlane.plane.visible = true;
                if (!wasVisible) {
                    //  console.log('🎯 Plan de segmentation activé - Pose détectée');
                }
                if (this.segmentationPlaneModelRef && this.multiModelManager) {
                    // D'abord essayer de trouver un modèle avec ce nom
                    let refModel = this.multiModelManager.getModelByName(this.segmentationPlaneModelRef);
                    let targetObject = null;
                    
                    if (refModel && refModel.model) {
                        // Modèle trouvé, utiliser sa position
                        targetObject = refModel.model;
                        console.log(`🎯 Référence trouvée: modèle "${this.segmentationPlaneModelRef}"`);
                    } else {
                        // Sinon, chercher un sous-objet avec ce nom
                        const subObjectResult = this.multiModelManager.getSubObjectByName(this.segmentationPlaneModelRef);
                        if (subObjectResult && subObjectResult.subObject) {
                            targetObject = subObjectResult.subObject;
                        //   console.log(`🎯 Référence trouvée: sous-objet "${this.segmentationPlaneModelRef}" dans le modèle "${subObjectResult.model.config?.modelName}"`);
                        }
                    }
                    
                    if (targetObject && targetObject.visible) {
                        this.segmentationPlane.plane.position.z = targetObject.position.z - 0.1;
                    }
                } else if (coordsResults.pose?.positionsPose2d?.center_torso) {
                    const centerTorso = coordsResults.pose.positionsPose2d.center_torso;
                    this.segmentationPlane.plane.position.z = centerTorso.z - 0.1;
                }
            } else {
                this.segmentationPlane.plane.visible = false;
                if (wasVisible) {
                    //  console.log('🎯 Plan de segmentation désactivé - Pose perdue');
                }
            }
        }
        // UPDATE ENVIRONEMENT
        if (this.environment && this.environment.update) this.environment.update();
        // UPDATE FACEGEO
        if (this.faceGeo && coordsResults.face?.threeLandmarks) {
            this.faceGeo.detectedUpdate(this.scene, coordsResults.face.threeLandmarks, width, height);
            if (this.faceVisible) this.faceGeo.faceAppear();
            else this.faceGeo.faceDisappear();
        }
        // Finalisation du rendu
        if (!this.project_settings.isOrthographic) this.camera.position.z = this.cameraDistance(height, this.project_settings.fov);
        // Extra safety check for materialManager
        if (this.materialManager && typeof this.materialManager.detectedUpdate === 'function') {
            try { this.materialManager.detectedUpdate(); } catch (error) { console.warn('Error calling materialManager.detectedUpdate():', error); }
        }
        
        // Mise à jour des OrbitControls
        if (this.controls) {
            this.controls.update();
        }
        
        this.rendererInstance.render();
    }

    /// CONFIGURATION DE LA DIRECTIONAL LIGHT
    setupDirectionalLight(lightConfig) {
        // Supprimer l'ancienne directional light si elle existe
        if (this.directionalLight) {
            this.scene.remove(this.directionalLight);
            this.directionalLight.dispose();
            this.directionalLight = null;
        }
        
        // Si pas de configuration, ne rien faire
        if (!lightConfig) return;
        
        // Extraire les paramètres avec des valeurs par défaut
        const {
            position = { x: 0, y: 100, z: 100 },
            color = 0xffffff,
            intensity = 1,
            target = { x: 0, y: 0, z: 0 } // Centre de la scène par défaut
        } = lightConfig;
        
        // Créer la directional light
        this.directionalLight = new THREE.DirectionalLight(color, intensity);
        this.directionalLight.position.set(position.x, position.y, position.z);
        
        // Configurer la cible (point d'intérêt)
        this.directionalLight.target.position.set(target.x, target.y, target.z);
        
        // Ajouter la lumière et sa cible à la scène
        this.scene.add(this.directionalLight);
        this.scene.add(this.directionalLight.target);
        
      //  console.log(`💡 Directional light créée - Position: (${position.x}, ${position.y}, ${position.z}), Couleur: ${color}, Intensité: ${intensity}`);
    }

    /// CALCUL DE LA DISTANCE CAMERA
    cameraDistance(height, fov) {
        return RenderingUtils.calculateCameraDistance(height, fov);
    }

    /// AJUSTEMENT DU CANVAS (RESIZE)
    adjustCanvas(width, height) {
        this.width = width;
        this.height = height;
        if (this.project_settings.isOrthographic) {
            this.camera.left = width / -2;
            this.camera.right = width / 2;
            this.camera.top = height / 2;
            this.camera.bottom = height / -2;
            this.camera.updateProjectionMatrix();
        } else {
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        }
        this.rendererInstance.adjustCanvas(width, height);
        if (this.materialManager) this.materialManager.adjustCanvas(width, height);
        if (this.videoBackground) this.videoBackground.plane.scale.set(width / this.videoBackground.width, height / this.videoBackground.height, 1);
        if (this.segmentationPlane) this.segmentationPlane.plane.scale.set(width / this.segmentationPlane.width, height / this.segmentationPlane.height, 1);
    }

    /// NETTOYAGE DE LA SCÈNE
    dispose() {
        // console.log(`🧹 Nettoyage de ThreeScene ${this.instanceId}`);
        
        // Supprimer l'event listener pour éviter l'accumulation
        if (this.threeCanvas && this.handleCanvasClick) {
            this.threeCanvas.removeEventListener('click', this.handleCanvasClick);
            // console.log(`🔇 Event listener de clic supprimé pour instance ${this.instanceId}`);
        }
        
        // Nettoyer le gestionnaire de modèles
        if (this.multiModelManager) {
            this.multiModelManager.clearModels();
            this.multiModelManager = null;
        }
        
        // Nettoyer le renderer et le postprocess AVANT les autres composants
        if (this.rendererInstance) {
            this.rendererInstance.dispose();
            this.rendererInstance = null;
        }
        
        // Nettoyer le gestionnaire de matériaux
        if (this.materialManager) {
            this.materialManager.dispose();
            this.materialManager = null;
        }
        
        // Nettoyer l'environnement (dispose des textures HDR/EXR)
        if (this.environment) {
            this.environment.dispose();
            this.environment = null;
        }
        
        // Nettoyer FaceGeo (dispose des textures et géométrie)
        if (this.faceGeo) {
            this.faceGeo.dispose();
            this.faceGeo = null;
        }
        
        // Nettoyer les VideoPlane (dispose des géométries et matériaux)
        if (this.videoBackground) {
            this.videoBackground.dispose();
            this.videoBackground = null;
        }
        
        if (this.segmentationPlane) {
            this.segmentationPlane.dispose();
            this.segmentationPlane = null;
        }
        
        // Nettoyer la directional light
        if (this.directionalLight) {
            this.scene.remove(this.directionalLight);
            if (this.directionalLight.target) {
                this.scene.remove(this.directionalLight.target);
            }
            this.directionalLight.dispose();
            this.directionalLight = null;
        }
        
        // Nettoyer la scène Three.js (objets restants)
        if (this.scene) {
            // Disposer récursivement tous les objets de la scène
            this.scene.traverse((child) => {
                if (child.geometry) {
                    child.geometry.dispose();
                }
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(material => {
                            if (material.map) material.map.dispose();
                            if (material.alphaMap) material.alphaMap.dispose();
                            material.dispose();
                        });
                    } else {
                        if (child.material.map) child.material.map.dispose();
                        if (child.material.alphaMap) child.material.alphaMap.dispose();
                        child.material.dispose();
                    }
                }
            });
        }
        
        // console.log(`✅ ThreeScene ${this.instanceId} nettoyée`);
    }
}